"use client";

/**
 * Client-side auth compat layer — same API surface as next-auth/react
 * (SessionProvider, useSession, signIn, signOut, getSession), backed by
 * Supabase Auth. Session data comes from /api/auth/session, which returns
 * the fully hydrated AppSession (same shape NextAuth produced).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getSupabaseBrowserClient } from "./supabase-browser";
import { DASHBOARD_HOME_PATH } from "@/lib/dashboard-routes";
import { clearDashboardStatsCache } from "@/lib/dashboard/clear-dashboard-stats-cache";
import type { AppSession } from "./types";

export type SessionStatus = "loading" | "authenticated" | "unauthenticated";

export interface UseSessionReturn {
  data: AppSession | null;
  status: SessionStatus;
  // next-auth compat: accepts (and ignores) partial session data — the
  // refetched /api/auth/session response is always the source of truth.
  update: (data?: unknown) => Promise<AppSession | null>;
}

const SessionContext = createContext<UseSessionReturn>({
  data: null,
  status: "loading",
  update: async () => null,
});

async function fetchSession(): Promise<AppSession | null> {
  try {
    const res = await fetch("/api/auth/session", { cache: "no-store" });
    if (!res.ok) return null;
    const session = (await res.json()) as AppSession | null;
    return session?.user ? session : null;
  } catch {
    return null;
  }
}

export function SessionProvider({
  children,
}: {
  children: React.ReactNode;
  // Accepted for next-auth/react compat; server-passed sessions are ignored
  // because /api/auth/session is the single source of truth.
  session?: unknown;
  basePath?: string;
  refetchInterval?: number;
  refetchOnWindowFocus?: boolean;
}) {
  const [data, setData] = useState<AppSession | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");

  const refresh = useCallback(async () => {
    const session = await fetchSession();
    setData(session);
    setStatus(session ? "authenticated" : "unauthenticated");
    return session;
  }, []);

  useEffect(() => {
    refresh();

    const supabase = getSupabaseBrowserClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Skip the mount-time event; the initial refresh() above covers it.
      if (event === "INITIAL_SESSION") return;
      if (
        event === "SIGNED_IN" ||
        event === "SIGNED_OUT" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        refresh();
      }
    });

    return () => subscription.unsubscribe();
  }, [refresh]);

  return (
    <SessionContext.Provider value={{ data, status, update: refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): UseSessionReturn {
  return useContext(SessionContext);
}

export async function getSession(): Promise<AppSession | null> {
  return fetchSession();
}

export interface SignInOptions {
  callbackUrl?: string;
  redirect?: boolean;
  email?: string;
  [key: string]: unknown;
}

/**
 * next-auth/react-compatible signIn. OAuth providers redirect through
 * Supabase; "email" sends a magic link via signInWithOtp.
 */
export async function signIn(
  provider?: string,
  options?: SignInOptions
): Promise<{ error: string | null; ok: boolean }> {
  const supabase = getSupabaseBrowserClient();
  const callbackUrl = options?.callbackUrl || DASHBOARD_HOME_PATH;
  const redirectTo = `${window.location.origin}/api/auth/callback?next=${encodeURIComponent(callbackUrl)}`;

  if (provider === "google" || provider === "github") {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (error) return { error: error.message, ok: false };
    return { error: null, ok: true }; // browser is redirecting
  }

  if (provider === "email") {
    if (!options?.email) return { error: "Email is required", ok: false };
    const { error } = await supabase.auth.signInWithOtp({
      email: options.email,
      options: { emailRedirectTo: redirectTo },
    });
    return { error: error?.message ?? null, ok: !error };
  }

  return { error: `Unsupported provider: ${provider}`, ok: false };
}

export interface SignOutOptions {
  callbackUrl?: string;
  redirect?: boolean;
}

export async function signOut(options?: SignOutOptions): Promise<void> {
  const supabase = getSupabaseBrowserClient();
  // Drop account-scoped browser snapshots so the next login never paints
  // the previous user's dashboard KPIs from localStorage.
  clearDashboardStatsCache();
  // Impersonation must not survive an explicit sign-out, or the same admin
  // signing back in within the cookie's lifetime would silently land in the
  // customer's account again. The cookie is httpOnly, so only the server can
  // clear it — and this must run BEFORE signOut while the request is still
  // authenticated. Best-effort: a failure never blocks logout, and the call
  // does no database work when there is no impersonation cookie.
  try {
    await fetch("/api/admin/impersonate/stop", { method: "POST" });
  } catch {
    // Ignore — signing out is more important than a clean audit record here.
  }
  await supabase.auth.signOut();
  if (options?.redirect === false) return;
  window.location.href = options?.callbackUrl || "/login";
}
